import type { KeyboardEvent, RefObject } from 'react'
import React, { useCallback, useEffect } from 'react'

import { ContentEditable } from '@/components/content-editable'

import { cn } from '@/utils'

import { isAtStart, isAtEnd, placeCaret } from '@/shared/lib/selection-caret'

import { HEADING_COLUMN_ID } from '@/entities/rundown'

import type { KeyboardNavigationFocusPayload } from '@/features/keyboard-navigation-handler'
import {
  keyboardNavigationEventBus,
  KeyboardNavigationEvent,
  KeyboardNavigationInput,
  KeyboardNavigationDirection,
} from '@/features/keyboard-navigation-handler'
import { useCueId } from '@/features/rundown/cue/cue.context'

export type CueSubtitleProps = {
  value?: string
  onChange: (value?: string) => void | Promise<void>
  disabled?: boolean
  readonly?: boolean
  className?: string
  inputRef: RefObject<HTMLDivElement | null>
  // Used to move focus to the title on ArrowUp from start of subtitle
  titleRef?: RefObject<HTMLDivElement | null>
}

export const CueSubtitle: React.FC<CueSubtitleProps> = ({
  value,
  onChange,
  disabled = false,
  readonly = false,
  className,
  inputRef,
  titleRef,
}) => {
  const cueId = useCueId()

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const el = inputRef.current

      if (!el) return

      const emit = (direction: KeyboardNavigationDirection) => {
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction,
          fromCueId: cueId,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.SUBTITLE,
        })
      }

      if (event.key === 'Tab') {
        emit(
          event.shiftKey
            ? KeyboardNavigationDirection.REVERSE
            : KeyboardNavigationDirection.FORWARD,
        )
        event.preventDefault()
        return
      }

      if (event.key === 'ArrowLeft' && isAtStart(el)) {
        emit(KeyboardNavigationDirection.LEFT)
        event.preventDefault()
        return
      }

      if (
        event.key === 'ArrowRight' &&
        (isAtEnd(el) || el.innerText.trim().length === 0)
      ) {
        emit(KeyboardNavigationDirection.RIGHT)
        event.preventDefault()
        return
      }

      if (event.key === 'ArrowUp' && isAtStart(el)) {
        const titleEl = titleRef?.current
        if (titleEl) {
          placeCaret(titleEl, 'end')
        } else {
          emit(KeyboardNavigationDirection.UP)
        }
        event.preventDefault()
        return
      }

      if (
        event.key === 'ArrowDown' &&
        (isAtEnd(el) || el.innerText.trim().length === 0)
      ) {
        emit(KeyboardNavigationDirection.DOWN)
        event.preventDefault()
        return
      }
    },
    [cueId, inputRef, titleRef],
  )

  // Focus handler: focus subtitle when header is targeted with headerSection==='subtitle'
  useEffect(() => {
    const onFocusHeader = ({
      cueId: focusedCueId,
      columnId,
      focusPosition,
      inputType,
    }: KeyboardNavigationFocusPayload) => {
      if (cueId !== focusedCueId || columnId !== HEADING_COLUMN_ID) return
      if (inputType !== KeyboardNavigationInput.SUBTITLE) return
      const el = inputRef.current
      if (!el) return
      placeCaret(el, focusPosition === 'start' ? 'start' : 'end')
    }

    keyboardNavigationEventBus.on(KeyboardNavigationEvent.FOCUS, onFocusHeader)
    return () =>
      keyboardNavigationEventBus.off(
        KeyboardNavigationEvent.FOCUS,
        onFocusHeader,
      )
  }, [cueId, inputRef])

  return (
    <div className={cn('text-left', className)}>
      <ContentEditable
        ref={inputRef}
        onKeyDown={onKeyDown}
        value={value}
        placeholder={readonly ? '' : 'Subtitle'}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}
